import React, { HTMLAttributes, useEffect, useRef, useState } from 'react';
import Header from '#/components/Form/Header';
import useProtectedSpotify from '#/hooks/useProtectedAxios';
import { TRootState } from '#/redux/store';
import ApiManager from '#/services/api/ApiManager';
import { spotifyApiConfig } from '#/utils/constants';
import { useDispatch, useSelector } from 'react-redux';
import tinyColor from 'tinycolor2';
import Vibrant from 'node-vibrant';
import useProfile from '#/hooks/useProfile';
import PlayButton from '#/components/Form/PlayButton/PlayButton';
import LoadingPage from '#/components/Layout/LoadingPage/LoadingPage';
import TrackTableHead from '#/components/Layout/TracksTableHead/TracksTableHead';
import msToReadableLongTime from '#/utils/msToReadableLongTime';
import TrackListItem from '#/components/Layout/TrackListItem/TrackListItem';
import {
  createActionPlaylistReset,
  createActionPlaylistSetError,
  createActionPlaylistSetIsLiked,
  createActionPlaylistSetPlaylist,
  createActionPlaylistSetTrackFavorite,
  createActionPlaylistStartLoading,
  createActionPlaylistStopLoading,
} from '#/redux/actions/playlist';
import { IPlaylistResponse } from '#/types/API/playlist';
import { useParams } from 'react-router-dom';
import useDeviceId from '#/hooks/useDeviceId';
import {
  emptyContainsResponse,
  urisParameter,
} from '#/utils/spotifyApiHelpers';

const playlistSelector = (state: TRootState) => state.playlist;

type TColor = [number, number, number];

const Playlist = () => {
  const imageReference = useRef<HTMLImageElement>(null);

  const profile = useProfile();

  const [dominantColor, setDominantColor] = useState<undefined | TColor>();

  const playlistSlice = useSelector(playlistSelector);

  const { playlist, _error, _loading } = playlistSlice;

  const dispatch = useDispatch();

  const protectedSpotify = useProtectedSpotify();

  const { id } = useParams();

  const { retrieveDeviceId } = useDeviceId();

  const handleClickPlay = async () => {
    const apiManager = new ApiManager(spotifyApiConfig, protectedSpotify);

    const response = await apiManager.replaceEntity(
      'playerPlay',
      '',
      {
        context_uri: playlist?.uri,
      },
      {
        device_id: retrieveDeviceId(),
      }
    );
    if ('error' in response) {
      // TODO: handle error
      // eslint-disable-next-line no-console
      console.log('Cant reproduce');
    }
  };

  useEffect(() => {
    const getPlaylist = async () => {
      dispatch(createActionPlaylistStartLoading());
      if (id) {
        const apiMannager = new ApiManager(spotifyApiConfig, protectedSpotify);

        const responsePlaylist = await apiMannager.getEntity<IPlaylistResponse>(
          'playlists',
          id
        );

        if ('error' in responsePlaylist) {
          // TODO: handle error
          dispatch(createActionPlaylistSetError(true));
        } else {
          const playlistData = responsePlaylist.data;

          const responseIsFollowed = await apiMannager.getAllEntities<
            boolean[]
          >('libraryContains', {
            uris: urisParameter('playlist', id),
          });

          const playlistTrackIds = playlistData.tracks.items
            .slice(0, 40)
            .map((item) => item.track.id);
          const responseFavTracks =
            playlistTrackIds.length > 0
              ? await apiMannager.getAllEntities<boolean[]>('libraryContains', {
                  uris: urisParameter('track', playlistTrackIds),
                })
              : await emptyContainsResponse();

          if ('error' in responseIsFollowed || 'error' in responseFavTracks) {
            // TODO: handle error
            dispatch(createActionPlaylistSetError(true));
          } else {
            const isLiked = responseIsFollowed.data[0];

            const newPlaylist = { ...playlistData, liked: isLiked };

            const tracksWithFavorite = newPlaylist.tracks.items.map(
              (item, index) => ({
                ...item,
                track: {
                  ...item.track,
                  favorite: responseFavTracks.data[index],
                },
              })
            );

            dispatch(
              createActionPlaylistSetPlaylist({
                ...newPlaylist,
                tracks: { ...newPlaylist.tracks, items: tracksWithFavorite },
              })
            );

            dispatch(createActionPlaylistStopLoading());
          }
        }
      }
    };

    getPlaylist();
  }, [dispatch, id, profile?.id, protectedSpotify]);

  useEffect(() => {
    if (playlist) {
      Vibrant.from(playlist.images[0].url)
        .getPalette()
        .then((palette) => setDominantColor(palette.Vibrant?.getRgb()));
    }
  }, [playlist]);

  useEffect(() => {
    return () => {
      dispatch(createActionPlaylistReset());
    };
  }, [dispatch]);

  const handleClickLike = async () => {
    const apiMannager = new ApiManager(spotifyApiConfig, protectedSpotify);
    if (playlist?.liked) {
      const responseRemoveFromFavorites = await apiMannager.deleteEntity(
        'library',
        '',
        {
          uris: urisParameter('playlist', id ?? ''),
        }
      );

      if ('error' in responseRemoveFromFavorites) {
        //TODO: handle error
      } else {
        dispatch(
          createActionPlaylistSetIsLiked({
            liked: false,
          })
        );
      }
    } else {
      const responseAddToFavorites = await apiMannager.replaceEntity(
        'library',
        '',
        undefined,
        {
          uris: urisParameter('playlist', id ?? ''),
        }
      );

      if ('error' in responseAddToFavorites) {
        //TODO: handle error
      } else {
        //
        dispatch(
          createActionPlaylistSetIsLiked({
            liked: true,
          })
        );
      }
    }
  };

  const getBannerStyle = () => {
    const style: HTMLAttributes<HTMLDivElement>['style'] = {};
    if (dominantColor) {
      const color = tinyColor(`rgb(${dominantColor.join(', ')})`);
      const darkerDominant = tinyColor(
        `rgb(${dominantColor.join(', ')})`
      ).darken(22);
      style.backgroundImage = `linear-gradient(${color.toRgbString()}, ${darkerDominant.toRgbString()})`;
    }
    return style;
  };

  const getBodyStyle = () => {
    const style: HTMLAttributes<HTMLDivElement>['style'] = {};
    if (dominantColor) {
      const darkerDominant = tinyColor(
        `rgb(${dominantColor.join(', ')})`
      ).darken(28);
      style.backgroundImage = `linear-gradient(${darkerDominant.toRgbString()}, transparent 240px)`;
    }
    return style;
  };

  return (
    <>
      {_loading || _error ? (
        <LoadingPage />
      ) : (
        <div className="mt-theme-top-bar-negative-height">
          <div className="pt-theme-top-bar-height" style={getBannerStyle()}>
            <div className="flex gap-6 pt-9 p-theme-page-padding">
              <div className=" w-[192px] 2xl:w-[232px]">
                <div className="w-full aspect-square overflow-hidden shadow-2xl shadow-theme-shadow">
                  <img
                    className="  h-full w-full object-cover"
                    ref={imageReference}
                    src={playlist?.images[0].url}
                    alt=""
                  />
                </div>
              </div>
              <div className=" flex-1 flex flex-col justify-end pb-1">
                <span className="font-bold text-sm">Playlist</span>
                <Header
                  type="h1"
                  className="text-6xl mb-9 font-bold tracking-tighter"
                >
                  {playlist?.name}
                </Header>
                <div className="flex gap-1 text-sm items-center">
                  <span className="font-bold">
                    {playlist?.owner.display_name}
                  </span>
                  •{' '}
                  <span>{` ${playlist?.followers.total} like${
                    playlist && playlist.followers.total > 0 ? 's' : ''
                  }`}</span>
                  •<span>{playlist?.tracks.total} songs,</span>
                  <span className=" text-theme-light-gray">
                    About{' '}
                    {msToReadableLongTime(
                      playlist
                        ? playlist.tracks.items
                            .map((item) => item.track.duration_ms)
                            .reduce((sum, current) => sum + current)
                        : 0
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div
            className="min-h-full p-theme-page-padding"
            style={getBodyStyle()}
          >
            <div className="flex flex-row items-center gap-8 mb-theme-page-padding">
              <PlayButton onClick={handleClickPlay} />
              <span
                onClick={handleClickLike}
                className={`material-icons text-[32px] cursor-default ${
                  playlist?.liked
                    ? 'text-theme-green'
                    : 'text-theme-light-gray hover:text-theme-white'
                } `}
                //onClick={onClickFavorite}
              >
                {playlist?.liked ? 'favorite' : 'favorite_border'}
              </span>
            </div>
            <div className=" mb-10">
              <TrackTableHead album dateAdded />

              {playlist?.tracks.items?.map((item, index) => {
                const handleClickFavoriteTrack = async () => {
                  const apiMannager = new ApiManager(
                    spotifyApiConfig,
                    protectedSpotify
                  );
                  if (item.track.favorite) {
                    const responseRemoveFromFavorites =
                      await apiMannager.deleteEntity('library', '', {
                        uris: urisParameter('track', item.track.id),
                      });

                    if ('error' in responseRemoveFromFavorites) {
                      //TODO: handle error
                    } else {
                      dispatch(
                        createActionPlaylistSetTrackFavorite({
                          track: item.track.id,
                          favorite: false,
                        })
                      );
                    }
                  } else {
                    const responseAddToFavorites =
                      await apiMannager.replaceEntity(
                        'library',
                        '',
                        undefined,
                        {
                          uris: urisParameter('track', item.track.id),
                        }
                      );

                    if ('error' in responseAddToFavorites) {
                      //TODO: handle error
                    } else {
                      //
                      dispatch(
                        createActionPlaylistSetTrackFavorite({
                          track: item.track.id,
                          favorite: true,
                        })
                      );
                    }
                  }
                };

                const dateAdded = new Date(item.added_at);

                return (
                  <TrackListItem
                    uri={item.track.uri}
                    contextUri={item.track.album.uri}
                    id={item.track.id}
                    image={item.track.album.images[1].url}
                    key={item.track.id}
                    name={item.track.name}
                    duration={item.track.duration_ms}
                    explicit={item.track.explicit}
                    number={index + 1}
                    followed={item.track.favorite ?? false}
                    artists={item.track.artists.map((artist) => ({
                      name: artist.name,
                      id: artist.id,
                    }))}
                    album={{
                      name: item.track.album.name,
                      id: item.track.album.id,
                    }}
                    dateAdded={dateAdded.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                    onFavorite={handleClickFavoriteTrack}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Playlist;
